import { Plus, Edit2, Trash2, AlertTriangle } from 'lucide-react'
import {
  calcPumpSales, calcTankSales, calcRevenue, calcMeasuredExcess, calcExpectedExcess,
  calcMeasuredExcessProfit, calcExpectedExcessProfit, calcExcessStatus,
} from '@petrotrack/shared'
import { formatNaira, formatLiters, FUEL_COLORS } from '../../utils/formatters'

const STATUS_CLS = { ok: 'text-green-600', shortfall: 'text-amber-700', over_limit: 'text-red-600' }

function Cell({ label, value, cls = 'text-gray-800' }) {
  return <div className="bg-gray-50 rounded-lg p-2.5"><p className="text-xs text-gray-400 mb-1 leading-none">{label}</p><p className={`font-semibold text-xs sm:text-sm ${cls}`}>{value}</p></div>
}

// `entries` = this product's entries in scope (today = one; analytics range = many).
// `setupMissing` = human fragments of missing equipment for this product
// (e.g. ["a PMS pump"]) — when non-empty the entry button becomes a
// "Setup needed" action that routes through the entry gate.
export default function ProductCard({ product, entries, latest, isAdmin, canEnter, canEdit, setupMissing = [], onEnter, onEdit, onDelete }) {
  const c = FUEL_COLORS[product] || FUEL_COLORS.PMS
  const sum = (fn) => entries.reduce((s, e) => s + fn(e), 0)
  const pumpSales = sum(calcPumpSales), tankSales = sum(calcTankSales)
  const measuredExcess = pumpSales - tankSales
  const measuredProfit = sum(calcMeasuredExcessProfit)
  const expectedProfit = sum(calcExpectedExcessProfit)
  const revenue = sum(calcRevenue)
  const hasComplete = entries.some((e) => e.session === 'complete')
  const status = entries.length === 1 ? calcExcessStatus(entries[0]) : (measuredExcess < sum(calcExpectedExcess) ? 'shortfall' : 'ok')

  return (
    <div className="border border-gray-200 rounded-xl p-3">
      <div className="flex items-center justify-between mb-3">
        <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${c.bg} ${c.text}`}>{product}</span>
        <div className="flex items-center gap-1">
          {canEdit && latest && (<>
            <button onClick={() => onEdit(latest)} className="p-1.5 text-blue-500 hover:bg-blue-50 rounded"><Edit2 className="w-3.5 h-3.5" /></button>
            <button onClick={() => onDelete(latest)} className="p-1.5 text-red-400 hover:bg-red-50 rounded"><Trash2 className="w-3.5 h-3.5" /></button>
          </>)}
          {setupMissing.length > 0 ? (
            <button onClick={() => (canEnter || isAdmin) && onEnter(product)}
              title={`Missing ${setupMissing.join(' and ')}`}
              className="flex items-center gap-1 bg-amber-100 text-amber-800 text-xs font-semibold px-3 py-1.5 rounded-xl border border-amber-200 hover:bg-amber-200 transition">
              <AlertTriangle className="w-3.5 h-3.5" /> Setup needed
            </button>
          ) : canEnter && (
            <button onClick={() => onEnter(product)} className="flex items-center gap-1 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold px-3 py-1.5 rounded-xl">
              <Plus className="w-3.5 h-3.5" />{hasComplete ? 'Edit' : 'Enter Data'}
            </button>
          )}
        </div>
      </div>
      {hasComplete ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          <Cell label="Tank Sales" value={formatLiters(tankSales)} />
          <Cell label="Pump Sales" value={formatLiters(pumpSales)} />
          <Cell label="Excess (L)" value={formatLiters(measuredExcess)} cls={STATUS_CLS[status]} />
          <Cell label="Excess Profit" value={formatNaira(measuredProfit)} cls={measuredProfit >= 0 ? 'text-gray-800' : 'text-red-600'} />
          {isAdmin && <Cell label="Expected" value={formatNaira(expectedProfit)} cls="text-gray-500" />}
          <Cell label="Revenue" value={formatNaira(revenue)} />
        </div>
      ) : (
        <p className="text-center text-sm text-gray-400 py-3 bg-gray-50 rounded-lg">{latest?.session === 'morning_open' ? 'Morning recorded — complete this evening' : 'No data yet'}</p>
      )}
    </div>
  )
}
